<template>
    <l-map
        ref="map"
        :center="[58.97, 5.7331]"
        :zoom="15"
        :options="mapOptions"
        @ready="onMapReady"
        @update:center="center = $event"
        @update:zoom="zoom = $event"
        @update:bounds="bounds = $event"
    >
        <template v-if="ready">
            <l-google-mutant v-bind="googleMutantOptions" :class="cssClasses" />
            <l-control-attribution
                position="bottomright"
                prefix="<a href='https://leafletjs.com' target='_blank' title='A JavaScript library for interactive maps'>Leaflet</a>"
            />
            <l-control v-if="!isMobile" position="bottomright" class="d-flex flex-column">
                <v-btn v-if="canUseGeolocation" fab class="ma-1" icon="mdi-crosshairs-gps" size="large" @click="$emit('locate-user')" />
                <v-btn fab class="ma-1" icon="mdi-plus" size="large" @click="zoomIn" />
                <v-btn fab class="ma-1" icon="mdi-minus" size="large" @click="zoomOut" />
            </l-control>
            <geolocation-marker v-if="userLocation" :model-value="userLocation" />
            <slot />
        </template>
    </l-map>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css';
import 'leaflet-contextmenu/dist/leaflet.contextmenu.min.css';
import 'leaflet-contextmenu';
import { LMap, LControl, LControlAttribution } from '@vue-leaflet/vue-leaflet';
import { ContextMenuItem, LatLngBoundsExpression, LatLngExpression, Map, MapOptions, PointTuple, latLngBounds } from 'leaflet';
import { computed, nextTick, ref, watch } from 'vue';
import LGoogleMutant from '@geta/vue-leaflet-googlemutant';
import { GeolocationMarker } from '..';
import { isLatLngBounds, isLeafletMap, useMapHelpers } from './mapHelpers';
import { isEmpty, isNumber, merge } from 'lodash-es';
import { defaultPadding } from '@/features/map/composables';
import { useDisplay } from 'vuetify';

const ANIMATION_DURATION = 0.2; // animation duration in seconds

interface GoogleMutantOptions {
    apiKey: string;
    mapId?: string;
}

interface Props {
    userLocation?: GeolocationCoordinates | null;
    canUseGeolocation?: boolean;
    grayscale?: boolean;
    contextmenu?: ContextMenuItem[];
    padding?: PointTuple;
    paddingTopLeft?: PointTuple;
    paddingBottomRight?: PointTuple;
    googleMutantOptions?: GoogleMutantOptions;
}

interface Emits {
    (e: 'locate-user'): void;
}

const props = withDefaults(defineProps<Props>(), {
    padding: () => [defaultPadding, defaultPadding],
    contextmenu: () => []
});
defineEmits<Emits>();

const center = defineModel<LatLngExpression>('center', { required: true });
const zoom = defineModel<number>('zoom', { required: true });
const bounds = defineModel<LatLngBoundsExpression>('bounds', { required: true });

const map = ref<typeof LMap>();
const ready = ref(false);

const { mobile: isMobile } = useDisplay();

const { addOffsets } = useMapHelpers(map);

const googleMutantOptions = computed(() => ({
    loaderOptions: {
        apiKey: props.googleMutantOptions?.apiKey || ''
    },
    mapId: props.googleMutantOptions?.mapId
}));

const cssClasses = computed(() => ({ 'tilelayer--grayscale': props.grayscale }));

const mapOptions: MapOptions & { useGlobalLeaflet: boolean } = {
    useGlobalLeaflet: false,
    attributionControl: false,
    zoomControl: false,
    contextmenu: true,
    contextmenuItems: props.contextmenu || []
};

function onMapReady(_map: Map) {
    // add additional map panes
    _map.createPane('servicejourneymarkersPane');
    _map.createPane('servicejourneypolylinesPane');

    // set up watcher for contextmenu
    watch(
        () => props.contextmenu,
        value => {
            if (!map.value?.leafletObject) return;

            // reset menu items
            map.value.leafletObject.contextmenu.removeAllItems();

            if (!value?.length) return;

            // add menu items
            value.forEach(item => map.value!.leafletObject.contextmenu.addItem(item));
        },
        {
            immediate: true
        }
    );

    nextTick(() => {
        // set initial bounds
        bounds.value = _map.getBounds();

        ready.value = true;
    });
}

function flyTo(latLng: LatLngExpression, newZoom?: number) {
    nextTick(() => {
        if (!map.value || !latLng) return;

        const leafletObject = map.value.leafletObject as Map;
        const { padding, paddingTopLeft = padding, paddingBottomRight = padding } = props;
        newZoom = newZoom || zoom.value;

        leafletObject.flyTo(addOffsets(latLng, newZoom, { padding, paddingTopLeft, paddingBottomRight }), newZoom, {
            animate: true,
            duration: ANIMATION_DURATION
        });
    });
}

function fitBounds(
    value: LatLngBoundsExpression,
    options?: { maxZoom?: number; padding?: PointTuple; paddingTopLeft?: PointTuple; paddingBottomRight?: PointTuple }
) {
    nextTick(() => {
        // convert to LatLngBounds instance
        if (!isLatLngBounds(value)) value = latLngBounds(value);

        if (!map.value || !value || !value.isValid()) return;

        const leafletObject = map.value.leafletObject as Map;
        const { padding, paddingTopLeft = padding, paddingBottomRight = padding } = props;
        options = merge({ padding, paddingTopLeft, paddingBottomRight, duration: ANIMATION_DURATION }, options);

        leafletObject.flyToBounds(value, options);
    });
}

function zoomIn() {
    zoom.value++;
}

function zoomOut() {
    zoom.value--;
}

function triggerResize() {
    map.value?.leafletObject?.invalidateSize();
}

watch(
    () => props.userLocation,
    value =>
        nextTick(() => {
            if (!value) return;

            flyTo({ lat: value.latitude, lng: value.longitude }, 15);
        }),
    { immediate: true }
);

watch(bounds, value => {
    if (!map.value || !isLeafletMap(map.value.leafletObject) || isEmpty(value)) return;

    // need to check if paddings have changed, and update as necessary
    if (map.value.leafletObject.getBounds().equals(value)) return;

    nextTick(() => fitBounds(value, { maxZoom: 18 }));
});

// set up watchers for center and zoom that updates the map state
watch(
    [center, zoom],
    value => {
        const [newCenter, newZoom] = value;
        if (
            !map.value ||
            !isLeafletMap(map.value.leafletObject) ||
            isEmpty(newCenter) ||
            !isNumber(newZoom) ||
            (map.value.leafletObject.getCenter().equals(newCenter) && map.value.leafletObject.getZoom() === newZoom)
        )
            return;

        nextTick(() => flyTo(newCenter, newZoom));
    },
    {
        immediate: true
    }
);

defineExpose({ fitBounds, flyTo, triggerResize });
</script>

<style lang="scss">
.leaflet-tile-pane {
    transition:
        0.2s filter linear,
        0.2s -webkit-filter linear;
}

.leaflet-overlay-pane,
.leaflet-marker-pane,
.leaflet-servicejourneymarkers-pane,
.leaflet-servicejourneypolylines-pane {
    svg {
        z-index: inherit;
    }
}

.leaflet-servicejourneymarkers-pane {
    z-index: 360;
}

.leaflet-servicejourneypolylines-pane {
    z-index: 350;
}

.leaflet-container:has(.tilelayer--grayscale) .leaflet-tile-pane {
    filter: grayscale(1);
}

// override default context menu styles
.leaflet-contextmenu {
    padding: 8px 0;

    a.leaflet-contextmenu-item {
        font-size: 1.1rem;
    }
}
</style>
